import React, { useState, useEffect, useCallback, useMemo } from 'react';
import TheLocker      from './TheLocker';
import GlobalPlayerCard from './GlobalPlayerCard';
import UpgradeOptions from './UpgradeOptions';
import Flashcards     from '../pages/Flashcards';
import Quiz           from '../pages/Quiz';
import SqlExercise    from '../pages/SqlExercise';
import { THEME_A }   from '../themeAConfig';
import { calcThemeBProgress } from '../gamificationConfig';
import { ALL_TOPICS, isTheoryAccessible }  from '../curriculumConfig';
import { authHeaders } from '../authToken';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Icons ─────────────────────────────────────────────────────────────────────

function HamburgerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="3" y1="6"  x2="21" y2="6"  />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <line x1="18" y1="6"  x2="6"  y2="18" />
      <line x1="6"  y1="6"  x2="18" y2="18" />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s', flexShrink: 0 }}>
      <polyline points="3 6 8 11 13 6" />
    </svg>
  );
}

// ── Side drawer ───────────────────────────────────────────────────────────────

function MobileDrawer({ open, onClose, onNavigateToCPUS, onNavigateToPacks, onNavigateToHanoi, onNavigateToFractals, onNavigateToDiffusion, onNavigateToGan, onNavigateToConditional, onNavigateToLatent, onNavigateToFlow, onNavigateToCaverns, onNavigateToBits, onNavigateToHelp, onNavigateToTeacher, isTeacher = false, onNavigateToAdmin, isAdmin = false, onLogout }) {
  // Collapsible "Simulators" group; auto-open when already on a simulator page.
  const SIM_PATHS = ['/cpus', '/packs', '/hanoi', '/fractals', '/diffusion', '/gan', '/conditional', '/latent', '/reversible', '/caverns'];
  const [simsOpen, setSimsOpen] = useState(() => SIM_PATHS.includes((window.location.pathname || '').replace(/\/+$/, '')));
  return (
    <>
      <div
        className={`m-drawer-backdrop${open ? ' m-drawer-backdrop-open' : ''}`}
        aria-hidden="true"
        onClick={onClose}
      />
      <aside className={`m-drawer${open ? ' m-drawer-open' : ''}`} aria-label="Navigation">
        <div className="m-drawer-hdr">
          <span className="m-drawer-brand">brookebit</span>
          <button className="icon-btn" onClick={onClose} aria-label="Close menu"><XIcon /></button>
        </div>
        {(isTeacher || isAdmin) && (
          <>
            <div className="m-drawer-eyebrow">{isAdmin ? 'Manage' : 'Teaching'}</div>
            <nav className="m-drawer-nav">
              {isTeacher && (
                <button className="m-drawer-item" onClick={() => { onNavigateToTeacher?.(); onClose(); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M2 4.5 L8 2 L14 4.5 L8 7 Z" />
                    <path d="M4.5 6 V9.5 C4.5 10.5 11.5 10.5 11.5 9.5 V6" />
                  </svg>
                  My classes
                </button>
              )}
              {isAdmin && (
                <button className="m-drawer-item" onClick={() => { onNavigateToAdmin?.(); onClose(); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="8" cy="5" r="2.4" />
                    <path d="M3.5 13c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
                  </svg>
                  Admin
                </button>
              )}
            </nav>
          </>
        )}
        <div className="m-drawer-eyebrow">External tools</div>
        <nav className="m-drawer-nav">
          <button className="m-drawer-item" aria-expanded={simsOpen} onClick={() => setSimsOpen(o => !o)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="3" width="12" height="9" rx="1.5" />
              <polygon points="7,6 10,7.6 7,9.2" fill="currentColor" stroke="none" />
              <line x1="5.5" y1="14" x2="10.5" y2="14" />
            </svg>
            Simulators
            <svg className={`m-drawer-chevron${simsOpen ? ' m-drawer-chevron-open' : ''}`}
              width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 3 11 8 6 13" />
            </svg>
          </button>
          {simsOpen && (
          <div className="m-drawer-subgroup">
          <button className="m-drawer-item" onClick={() => { onNavigateToCPUS?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="12" height="12" rx="2" />
              <line x1="6" y1="6" x2="10" y2="6" />
              <line x1="6" y1="9" x2="9" y2="9" />
            </svg>
            CPU Simulator
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToPacks?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="3"  cy="8" r="1.5" />
              <circle cx="8"  cy="4" r="1.5" />
              <circle cx="8"  cy="12" r="1.5" />
              <circle cx="13" cy="8" r="1.5" />
              <line x1="4.5" y1="7.2"  x2="6.5"  y2="4.8"  />
              <line x1="4.5" y1="8.8"  x2="6.5"  y2="11.2" />
              <line x1="9.5" y1="4.8"  x2="11.5" y2="7.2"  />
              <line x1="9.5" y1="11.2" x2="11.5" y2="8.8"  />
            </svg>
            Packet Switching Lab
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToHanoi?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="1.5" y1="14" x2="14.5" y2="14" />
              <line x1="8" y1="2.5" x2="8" y2="14" />
              <rect x="4.5" y="10.5" width="7" height="2.2" rx="0.6" />
              <rect x="5.75" y="7.8" width="4.5" height="2.2" rx="0.6" />
              <rect x="6.5" y="5.1" width="3" height="2.2" rx="0.6" />
            </svg>
            Towers of Hanoi
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToFractals?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="8,2 14,13 2,13" />
              <polygon points="8,7.5 11,13 5,13" />
            </svg>
            Fractal Drawing
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToDiffusion?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="1.8" y="1.8" width="5.4" height="5.4" rx="0.8" />
              <line x1="9.6" y1="4.5" x2="14.2" y2="4.5" />
              <circle cx="11.9" cy="11.5" r="2.6" />
            </svg>
            Diffusion Denoising
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToGan?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="4" cy="8" r="2.4" />
              <circle cx="12" cy="8" r="2.4" />
              <path d="M6.4 6.6 L9.6 6.6 M6.4 9.4 L9.6 9.4" />
            </svg>
            GAN Loop
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToConditional?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="3" width="12" height="10" rx="1.5" />
              <path d="M5 10 L7 7 L9 9.5 L11 6.5" />
            </svg>
            Conditional Generation
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToLatent?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="12" height="12" rx="1.5" />
              <circle cx="6" cy="6" r="1" />
              <circle cx="11" cy="6" r="1" />
              <circle cx="8.5" cy="10.5" r="1.6" fill="currentColor" />
            </svg>
            VAE Latent Space
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToFlow?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 5.5 H11 M9 3.5 L11 5.5 L9 7.5" />
              <path d="M13 10.5 H5 M7 8.5 L5 10.5 L7 12.5" />
            </svg>
            Flow Reversibility
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToCaverns?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 6 H6 A4 4 0 0 1 6 12 H2 Z" />
              <line x1="6" y1="9" x2="10" y2="9" />
              <circle cx="12" cy="9" r="1.4" />
            </svg>
            Boolean Caverns
          </button>
          </div>
          )}
          <button className="m-drawer-item" onClick={() => { onNavigateToBits?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 4 2 8 6 12" />
              <polyline points="10 4 14 8 10 12" />
              <line x1="9" y1="2.5" x2="7" y2="13.5" />
            </svg>
            brookebits
          </button>
          <button className="m-drawer-item" onClick={() => { onNavigateToHelp?.(); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="8" cy="8" r="6.5" />
              <path d="M6.1 6.2a1.9 1.9 0 0 1 3.7.6c0 1.3-1.9 1.7-1.9 2.9" />
              <line x1="8" y1="11.6" x2="8" y2="11.7" />
            </svg>
            Help &amp; Guide
          </button>
        </nav>
        <div className="m-drawer-divider" />
        <button className="m-drawer-logout" onClick={() => { onLogout?.(); onClose(); }}>
          Log out
        </button>
      </aside>
    </>
  );
}

// ── Mobile Theory accordion ───────────────────────────────────────────────────

function MobileTheoryPicker({ activeLevel, onStudy, onReview, playerData, onUpsell }) {
  const [openTopics,    setOpenTopics]    = useState({ A1: true });
  const [openSubtopics, setOpenSubtopics] = useState({});
  // Map<scope → title> — supports multi-select
  const [selectedMap,   setSelectedMap]   = useState(new Map());
  const [dueCount,      setDueCount]      = useState(null);

  // Fetch spaced-repetition due count for the review CTA.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/theme-a/due-cards?level=${activeLevel}`, { headers: authHeaders() });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setDueCount(d.count ?? 0);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [activeLevel]);

  // Deselect HL-only statements when switching to SL
  useEffect(() => {
    if (activeLevel === 'sl') {
      setSelectedMap(prev => {
        const next = new Map(prev);
        THEME_A.forEach(topic => topic.subtopics.forEach(sub => {
          if (sub.hlOnly) sub.statements.forEach(s => next.delete(s.scope));
          else sub.statements.forEach(s => { if (s.hlOnly) next.delete(s.scope); });
        }));
        return next.size === prev.size ? prev : next;
      });
    }
  }, [activeLevel]);

  const toggleTopic = useCallback((id) => {
    setOpenTopics(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleSubtopicOpen = useCallback((scope) => {
    setOpenSubtopics(prev => ({ ...prev, [scope]: !prev[scope] }));
  }, []);

  const toggleStmt = useCallback((scope, title) => {
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (next.has(scope)) next.delete(scope); else next.set(scope, title);
      return next;
    });
  }, []);

  const toggleAllInSub = useCallback((sub, level) => {
    const visible = sub.statements.filter(s => !(level === 'sl' && s.hlOnly));
    setSelectedMap(prev => {
      const next = new Map(prev);
      const allSel = visible.every(s => next.has(s.scope));
      if (allSel) visible.forEach(s => next.delete(s.scope));
      else visible.forEach(s => next.set(s.scope, s.title));
      return next;
    });
  }, []);

  const selectedScopes = [...selectedMap.keys()].sort();
  const selCount = selectedMap.size;
  const ctaTitle = selCount === 0 ? ''
    : selCount === 1 ? [...selectedMap.values()][0]
    : `${selectedScopes[0]} & ${selCount - 1} more`;
  // SQL exercises only exist for the "Construct ..." statements A3.3.2/.3/.4,
  // so the SQL launcher appears only when the selection includes one of them.
  const hasSql = selectedScopes.some(s => /^A3\.3\.[234]\b/.test(s));

  return (
    <>
      {dueCount !== null && dueCount > 0 && (
        <button className="m-review-due-btn" onClick={onReview}>
          🔁 Review <strong>{dueCount}</strong> card{dueCount !== 1 ? 's' : ''} due
        </button>
      )}
      <div className="m-theory-acc">
        {THEME_A.map(topic => {
          // Freemium: theory topics outside the free tier are Premium-locked for
          // un-entitled accounts (server enforces with 402). A locked header opens
          // the upsell instead of expanding.
          const locked = !isTheoryAccessible(playerData, topic.id);
          const topicOpen = !locked && !!openTopics[topic.id];
          return (
            <div key={topic.id} className={`m-tacc-topic${locked ? ' m-tacc-topic-premium' : ''}`}>
              <button
                className="m-tacc-topic-hdr"
                onClick={() => locked ? onUpsell?.() : toggleTopic(topic.id)}
                aria-expanded={topicOpen}
              >
                <span className="m-tacc-icon" aria-hidden="true">{topic.icon}</span>
                <span className="m-tacc-topic-title">{topic.title}</span>
                {locked && <span className="m-tacc-premium-badge">Premium</span>}
                {locked
                  ? <span className="m-tacc-lock" aria-hidden="true">🔒</span>
                  : <ChevronIcon open={topicOpen} />}
              </button>

              {topicOpen && (
                <div className="m-tacc-topic-body">
                  {topic.subtopics.map(sub => {
                    const subMuted     = activeLevel === 'sl' && sub.hlOnly;
                    const subOpen      = !!openSubtopics[sub.scope];
                    const visibleStmts = sub.statements.filter(s => !(activeLevel === 'sl' && s.hlOnly));
                    const subSelCount  = visibleStmts.filter(s => selectedMap.has(s.scope)).length;
                    const subAllSel    = subSelCount === visibleStmts.length && visibleStmts.length > 0;
                    const subSomeSel   = subSelCount > 0 && !subAllSel;

                    return (
                      <div key={sub.scope} className={`m-tacc-sub${subMuted ? ' m-tacc-muted' : ''}`}>
                        {/* Subtopic row: checkbox + expand toggle */}
                        <div className="m-tacc-sub-hdr-row">
                          <button
                            className={[
                              'm-tacc-sub-check-btn',
                              subAllSel  ? 'm-check-on'      : '',
                              subSomeSel ? 'm-check-partial'  : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => !subMuted && toggleAllInSub(sub, activeLevel)}
                            disabled={subMuted}
                            aria-label={`Select all in ${sub.scope}`}
                            aria-pressed={subAllSel || subSomeSel}
                          >
                            {subAllSel  && <span className="m-check-mark">✓</span>}
                            {subSomeSel && <span className="m-check-mark m-check-dash">—</span>}
                          </button>
                          <button
                            className="m-tacc-sub-expand"
                            onClick={() => !subMuted && toggleSubtopicOpen(sub.scope)}
                            aria-expanded={subOpen}
                            disabled={subMuted}
                          >
                            <code className="m-tacc-chip">{sub.scope}</code>
                            <span className="m-tacc-sub-title">
                              {sub.title.replace(`${sub.scope} `, '')}
                              {sub.hlOnly && <span className="m-hl-badge">HL</span>}
                            </span>
                            {!subMuted && <ChevronIcon open={subOpen} />}
                          </button>
                        </div>

                        {subOpen && !subMuted && (
                          <div className="m-tacc-stmt-list">
                            {sub.statements.map(stmt => {
                              const stmtMuted  = activeLevel === 'sl' && stmt.hlOnly;
                              const isSelected = selectedMap.has(stmt.scope);
                              return (
                                <button
                                  key={stmt.scope}
                                  className={[
                                    'm-tacc-stmt',
                                    stmtMuted  ? 'm-tacc-muted'    : '',
                                    isSelected ? 'm-tacc-stmt-sel' : '',
                                  ].filter(Boolean).join(' ')}
                                  onClick={() => !stmtMuted && toggleStmt(stmt.scope, stmt.title)}
                                  disabled={stmtMuted}
                                  aria-pressed={isSelected}
                                >
                                  <span className={[
                                    'm-check',
                                    isSelected ? 'm-check-on' : '',
                                  ].filter(Boolean).join(' ')} aria-hidden="true">
                                    {isSelected && <span className="m-check-mark">✓</span>}
                                  </span>
                                  <code className="m-tacc-chip">{stmt.scope}</code>
                                  <span className="m-tacc-stmt-title">
                                    {stmt.title}
                                    {stmt.hlOnly && !sub.hlOnly && (
                                      <span className="m-hl-badge" style={{ marginLeft: 5 }}>HL</span>
                                    )}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Fixed bottom CTA when ≥1 statement selected */}
      {selCount > 0 && (
        <div className="m-stmt-cta">
          <div className="m-stmt-cta-scope">
            <span className="m-stmt-cta-count">{selCount} selected</span>
            <span className="m-stmt-cta-title" title={ctaTitle}>{ctaTitle}</span>
          </div>
          <div className="m-stmt-cta-btns">
            <button
              className="m-cta-btn m-cta-btn-sec"
              onClick={() => onStudy('flashcards', selectedScopes, ctaTitle)}
            >
              Flashcards
            </button>
            <button
              className="m-cta-btn m-cta-btn-pri"
              onClick={() => onStudy('quiz', selectedScopes, ctaTitle)}
            >
              Quiz
            </button>
            {hasSql && (
              <button
                className="m-cta-btn m-cta-btn-pri"
                onClick={() => onStudy('sql', selectedScopes, ctaTitle)}
              >
                SQL
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── MobileView ────────────────────────────────────────────────────────────────

// Premium upsell shown when a free account taps a locked theory topic. Reuses the
// shared cd-upsell-* styles. The server is the real boundary; no live checkout yet.
function MobileUpsell({ onClose }) {
  return (
    <div className="cd-upsell-overlay" role="dialog" aria-modal="true" aria-label="Premium" onClick={onClose}>
      <div className="cd-upsell-card" onClick={(e) => e.stopPropagation()}>
        <div className="cd-upsell-icon" aria-hidden="true">🔒</div>
        <h2 className="cd-upsell-title">A Premium feature</h2>
        <p className="cd-upsell-body">
          Premium unlocks the full syllabus in both Python and Java, the Paper 2
          exam vault, and saving &amp; sharing your own code snippets.
        </p>
        <UpgradeOptions />
        <button className="cd-upsell-btn" onClick={onClose}>Keep exploring the free units</button>
      </div>
    </div>
  );
}


export default function MobileView({
  playerData,
  displayName,
  studentId,
  themeAAccess = false,
  theme,
  onToggleTheme,
  onLogout,
  onEquipCosmetic,
  activeLang,
  activeLevel,
  onLangChange,
  onLevelChange,
  onNavigateToCPUS,
  onNavigateToPacks,
  onNavigateToHanoi,
  onNavigateToFractals,
  onNavigateToDiffusion,
  onNavigateToGan,
  onNavigateToConditional,
  onNavigateToLatent,
  onNavigateToFlow,
  onNavigateToCaverns,
  onNavigateToBits,
  onNavigateToHelp,
  onNavigateToTeacher,
  isTeacher = false,
  onNavigateToAdmin,
  isAdmin = false,
}) {
  const [activeTab,   setActiveTab]   = useState('theory');
  const [studyView,   setStudyView]   = useState(null);
  const [upsellOpen,  setUpsellOpen]  = useState(false);
  const [drawerOpen,  setDrawerOpen]  = useState(false);

  const handleStudy = useCallback((mode, scopes, title) => {
    setStudyView({ mode, scopes, title });
  }, []);

  const handleReview = useCallback(() => {
    setStudyView({ mode: 'flashcards', scopes: [], title: 'Spaced repetition', reviewMode: true });
  }, []);

  const clearStudy = useCallback(() => setStudyView(null), []);

  const topicData = useMemo(() => {
    if (!playerData?.units) return {};
    const map = {};
    ALL_TOPICS.forEach(topic => {
      if (!topic.unitId) return;
      const resolvedUnitId =
        activeLang === 'java' && !topic.unitId.endsWith('_java')
          ? `${topic.unitId}_java`
          : topic.unitId;
      const u = playerData.units[resolvedUnitId];
      if (u) {
        map[topic.id] = {
          completedExercises: u.completedExercises ?? u.completed_exercises ?? 0,
          totalExercises:     u.totalExercises     ?? u.total_exercises     ?? 0,
        };
      }
    });
    return map;
  }, [playerData, activeLang]);

  const themeBProgress  = calcThemeBProgress(topicData);

  // Theme A mastery — same source as the desktop dashboard player card.
  const [themeAOverall, setThemeAOverall] = useState(null);
  useEffect(() => {
    if (!studentId || !themeAAccess) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/theme-a/mastery/${studentId}?level=${activeLevel}`,
          { headers: authHeaders() }
        );
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setThemeAOverall(d?.overall?.percent ?? null);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [studentId, activeLevel, themeAAccess]);
  const themeAProgress = themeAOverall === null ? null : themeAOverall * 100;

  const isLoaded        = playerData?.lifetimeXP !== undefined;
  const mergedPlayer    = isLoaded
    ? { ...playerData, name: displayName || 'Student' }
    : null;

  // ── Study screen ────────────────────────────────────────────────────────────
  if (studyView) {
    const StudyScreen = studyView.mode === 'sql'  ? SqlExercise
                      : studyView.mode === 'quiz' ? Quiz
                      : Flashcards;
    return (
      <div className="mobile-view">
        <header className="mobile-topbar">
          <button className="m-back-btn" onClick={clearStudy} aria-label="Back to topics">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"><polyline points="10 3 5 8 10 13" /></svg>
            Back
          </button>
          <span className="m-study-heading">
            {studyView.mode === 'sql' ? 'SQL' : studyView.mode === 'quiz' ? 'Quiz' : 'Flashcards'}
          </span>
          <div className="m-level-mini">
            <button
              className={`m-level-btn${activeLevel === 'sl' ? ' m-level-btn-on' : ''}`}
              onClick={() => onLevelChange('sl')}
            >SL</button>
            <button
              className={`m-level-btn${activeLevel === 'hl' ? ' m-level-btn-on' : ''}`}
              onClick={() => onLevelChange('hl')}
            >HL</button>
          </div>
        </header>
        <div className="m-study-wrap">
          <StudyScreen
            scopes={studyView.scopes}
            level={activeLevel}
            scopeTitle={studyView.title}
            reviewMode={studyView.reviewMode}
            onBack={clearStudy}
          />
        </div>
      </div>
    );
  }

  // ── Main view ───────────────────────────────────────────────────────────────
  return (
    <div className="mobile-view">

      {/* Topbar */}
      <header className="mobile-topbar">
        <button
          className="icon-btn"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
        >
          <HamburgerIcon />
        </button>

        <div className="mobile-topbar-brand">
          <img src="/logo.svg" alt="brookebit" height={26} style={{ display: 'block' }} />
          <span className="mobile-topbar-title">IB CS</span>
        </div>

        <div className="mobile-topbar-actions">
          <button
            className="icon-btn"
            onClick={onToggleTheme}
            aria-label="Toggle theme"
          >
            {theme === 'light' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1"  x2="12" y2="3"  />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22"  y1="4.22"  x2="5.64"  y2="5.64"  />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1"  y1="12" x2="3"  y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36" />
                <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"  />
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* Compact player card */}
      {mergedPlayer && (
        <GlobalPlayerCard
          playerData={mergedPlayer}
          themeBProgress={themeBProgress}
          themeAProgress={themeAProgress}
          showThemeA={themeAAccess}
          compact
        />
      )}

      {/* Two-pill pivot */}
      <div className="m-pivot-bar">
        <button
          className={`m-pivot-pill${activeTab === 'theory' ? ' m-pivot-pill-on' : ''}`}
          onClick={() => setActiveTab('theory')}
        >
          📚 Theory Review
        </button>
        <button
          className={`m-pivot-pill${activeTab === 'locker' ? ' m-pivot-pill-on' : ''}`}
          onClick={() => setActiveTab('locker')}
        >
          🎒 The Locker
        </button>
      </div>

      {/* SL / HL toggle — only for Theory tab */}
      {activeTab === 'theory' && (
        <div className="m-level-bar">
          <span className="m-level-label">Level:</span>
          <div className="m-level-toggle">
            <button
              className={`m-level-btn${activeLevel === 'sl' ? ' m-level-btn-on' : ''}`}
              onClick={() => onLevelChange('sl')}
            >🟢 SL</button>
            <button
              className={`m-level-btn${activeLevel === 'hl' ? ' m-level-btn-on' : ''}`}
              onClick={() => onLevelChange('hl')}
            >⚡ HL</button>
          </div>
        </div>
      )}

      {/* Content area */}
      <div className="mobile-content">
        {activeTab === 'theory' && (
          <MobileTheoryPicker
            activeLevel={activeLevel}
            onStudy={handleStudy}
            onReview={handleReview}
            playerData={playerData}
            onUpsell={() => setUpsellOpen(true)}
          />
        )}

        {activeTab === 'locker' && mergedPlayer && (
          <TheLocker playerData={mergedPlayer} onEquipCosmetic={onEquipCosmetic} />
        )}

        {activeTab === 'locker' && !mergedPlayer && (
          <div className="mobile-loading">Loading your stats…</div>
        )}

        {/* Coding lab nudge at bottom of locker */}
        {activeTab === 'locker' && (
          <div className="mobile-desktop-cta">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            <span>Open on a laptop or desktop to do coding exercises</span>
          </div>
        )}
      </div>

      {/* Side drawer */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onNavigateToCPUS={onNavigateToCPUS}
        onNavigateToPacks={onNavigateToPacks}
        onNavigateToHanoi={onNavigateToHanoi}
        onNavigateToFractals={onNavigateToFractals}
        onNavigateToDiffusion={onNavigateToDiffusion}
        onNavigateToGan={onNavigateToGan}
        onNavigateToConditional={onNavigateToConditional}
        onNavigateToLatent={onNavigateToLatent}
        onNavigateToFlow={onNavigateToFlow}
        onNavigateToCaverns={onNavigateToCaverns}
        onNavigateToBits={onNavigateToBits}
        onNavigateToHelp={onNavigateToHelp}
        onNavigateToTeacher={onNavigateToTeacher}
        isTeacher={isTeacher}
        onNavigateToAdmin={onNavigateToAdmin}
        isAdmin={isAdmin}
        onLogout={onLogout}
      />

      {upsellOpen && <MobileUpsell onClose={() => setUpsellOpen(false)} />}

    </div>
  );
}
